import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bankingAPI } from '../../api';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent } from '../../components/ui/Card';
import { FormLabel } from '../../components/ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import TransactionDetailSlideOver from '../../components/banking/TransactionDetailSlideOver';
import DeleteTransactionDialog from '../../components/banking/DeleteTransactionDialog';
import TransactionEditForm from '../../components/banking/TransactionEditForm';
import VoidTransactionDialog from '../../components/banking/VoidTransactionDialog';
import { ArrowLeft, Printer, Download, FileText, Search, X } from 'lucide-react';

const BankingAccountTransactions = () => {
  const navigate = useNavigate();
  const { id: accountId } = useParams();
  
  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  
  // Transaction detail slide-over
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [showDetailSlideOver, setShowDetailSlideOver] = useState(false);
  
  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Edit form
  const [showEditForm, setShowEditForm] = useState(false);
  const [transactionToEdit, setTransactionToEdit] = useState(null);
  
  // Void dialog
  const [showVoidDialog, setShowVoidDialog] = useState(false);
  const [transactionToVoid, setTransactionToVoid] = useState(null);
  const [voidLoading, setVoidLoading] = useState(false);
  
  // Filters
  const [search, setSearch] = useState('');
  const [dateFilters, setDateFilters] = useState({
    start_date: '',
    end_date: ''
  });
  const [typeFilter, setTypeFilter] = useState('');
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  // Fetch account details
  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const data = await bankingAPI.getAccount(accountId);
        setAccount(data);
      } catch (err) {
        setError(err.message || 'Failed to load account details');
        console.error('Error fetching account:', err);
      }
    };
    
    fetchAccount();
  }, [accountId]);

  // Fetch transactions
  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };
      
      if (dateFilters.start_date) {
        params.start_date = dateFilters.start_date;
      }
      
      if (dateFilters.end_date) {
        params.end_date = dateFilters.end_date;
      }
      
      const response = await bankingAPI.getAccountTransactions(accountId, params);
      let transactionsList = response.transactions || [];
      
      // Client-side filtering for search and type
      if (search.trim()) {
        const searchLower = search.toLowerCase();
        transactionsList = transactionsList.filter(t => 
          (t.reference && t.reference.toLowerCase().includes(searchLower)) ||
          (t.description && t.description.toLowerCase().includes(searchLower)) ||
          (t.notes && t.notes.toLowerCase().includes(searchLower))
        );
      }
      
      if (typeFilter) {
        transactionsList = transactionsList.filter(t => t.transaction_type === typeFilter);
      }
      
      setTransactions(transactionsList);
      setPagination(prev => ({
        ...prev,
        ...response.pagination
      }));
    } catch (err) {
      setError(err.message || 'Failed to load transactions');
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  }, [accountId, pagination.page, pagination.limit, dateFilters, search, typeFilter]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getAccountTypeLabel = (type) => {
    const labels = {
      current: 'Current Account',
      savings: 'Savings Account',
      petty_cash: 'Petty Cash',
      proprietor_drawings: 'Proprietor Drawings',
      credit_card: 'Credit Card',
      payment_gateway: 'Payment Gateway',
      loan_account: 'Loan Account',
      other: 'Other'
    };
    return labels[type] || type;
  };

  const getTransactionTypeLabel = (type) => {
    const labels = {
      deposit: 'Deposit',
      withdrawal: 'Withdrawal',
      transfer: 'Transfer',
      manual: 'Manual'
    };
    return labels[type] || type;
  };

  const getTransactionTypeColor = (type, isIncoming) => {
    if (type === 'deposit' || (type === 'transfer' && isIncoming)) {
      return 'text-green-600 dark:text-green-400';
    }
    if (type === 'withdrawal' || (type === 'transfer' && !isIncoming)) {
      return 'text-red-600 dark:text-red-400';
    }
    return 'text-gray-600 dark:text-gray-400';
  };

  const handleClearFilters = () => {
    setSearch('');
    setDateFilters({ start_date: '', end_date: '' });
    setTypeFilter('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = search || dateFilters.start_date || dateFilters.end_date || typeFilter;

  // Handle transaction row click
  const handleTransactionClick = (transactionId) => {
    setSelectedTransactionId(transactionId);
    setShowDetailSlideOver(true);
  };

  // Handle slide-over close
  const handleDetailSlideOverClose = (open) => {
    setShowDetailSlideOver(open);
    if (!open) {
      setSelectedTransactionId(null);
    }
  };

  // Handle edit transaction - show edit form
  const handleEditTransaction = (transaction) => {
    setTransactionToEdit(transaction);
    setShowEditForm(true);
    setShowDetailSlideOver(false);
  };

  // Handle edit form close
  const handleEditFormClose = (open) => {
    if (!open) {
      setShowEditForm(false);
      setTransactionToEdit(null);
    }
  };

  // Handle edit success
  const handleEditSuccess = (updatedTransaction) => {
    setShowEditForm(false);
    setTransactionToEdit(null);
    fetchTransactions();
    // Refresh account balance
    bankingAPI.getAccount(accountId).then(setAccount).catch(console.error);
  };

  // Handle delete transaction - show confirmation dialog
  const handleDeleteTransaction = (transaction) => {
    setTransactionToDelete(transaction);
    setShowDeleteDialog(true);
  };

  // Confirm delete transaction
  const confirmDeleteTransaction = async () => {
    if (!transactionToDelete) return;
    
    try {
      setDeleteLoading(true);
      await bankingAPI.deleteTransaction(transactionToDelete.id);
      setShowDeleteDialog(false);
      setTransactionToDelete(null);
      setShowDetailSlideOver(false);
      setSelectedTransactionId(null);
      fetchTransactions();
    } catch (err) {
      setError(err.message || 'Failed to delete transaction');
      console.error('Error deleting transaction:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle delete dialog close
  const handleDeleteDialogClose = (open) => {
    if (!open && !deleteLoading) {
      setShowDeleteDialog(false);
      setTransactionToDelete(null);
    }
  };

  // Handle void transaction - show void dialog
  const handleVoidTransaction = (transaction) => {
    setTransactionToVoid(transaction);
    setShowVoidDialog(true);
    setShowDetailSlideOver(false);
  };

  // Handle void dialog close
  const handleVoidDialogClose = (open) => {
    if (!open && !voidLoading) {
      setShowVoidDialog(false);
      setTransactionToVoid(null);
    }
  };

  // Confirm void transaction
  const confirmVoidTransaction = async (reason) => {
    if (!transactionToVoid) return;
    
    try {
      setVoidLoading(true);
      await bankingAPI.voidTransaction(transactionToVoid.id, reason);
      setShowVoidDialog(false);
      setTransactionToVoid(null);
      setSelectedTransactionId(null);
      fetchTransactions();
      // Refresh account balance
      bankingAPI.getAccount(accountId).then(setAccount).catch(console.error);
    } catch (err) {
      setError(err.message || 'Failed to void transaction');
      console.error('Error voiding transaction:', err);
    } finally {
      setVoidLoading(false);
    }
  };

  // Calculate running balance for display
  const getRunningBalance = (index) => {
    // For simplicity, we'll calculate from current balance backwards
    // This is an approximation - for accurate running balance, we'd need the API to return it
    if (!account) return 0;
    
    let balance = parseFloat(account.current_balance || 0);
    
    // Subtract transactions from index 0 to current index
    for (let i = 0; i < index; i++) {
      const t = transactions[i];
      const isIncoming = t.transaction_type === 'deposit' || 
        (t.transaction_type === 'transfer' && t.linked_transfer_transaction_id);
      
      if (isIncoming) {
        balance -= parseFloat(t.amount || 0);
      } else {
        balance += parseFloat(t.amount || 0);
      }
    }
    
    return balance;
  };

  // Print handler
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to print');
      return;
    }

    const dateRange = dateFilters.start_date || dateFilters.end_date 
      ? `${dateFilters.start_date || 'Start'} to ${dateFilters.end_date || 'Present'}`
      : 'All Time';

    // Calculate summary totals
    let totalDeposits = 0;
    let totalWithdrawals = 0;
    
    transactions.forEach(t => {
      const isIncoming = t.transaction_type === 'deposit' || 
        (t.transaction_type === 'transfer' && t.linked_transfer_transaction_id);
      const amount = parseFloat(t.amount || 0);
      
      if (isIncoming) {
        totalDeposits += amount;
      } else {
        totalWithdrawals += amount;
      }
    });
    
    const netChange = totalDeposits - totalWithdrawals;

    // Calculate running balances for print
    const transactionsWithBalance = [];
    let runningBalance = parseFloat(account?.current_balance || 0);
    
    // Work backwards from current balance to get running balances
    for (let i = 0; i < transactions.length; i++) {
      const t = transactions[i];
      const isIncoming = t.transaction_type === 'deposit' || 
        (t.transaction_type === 'transfer' && t.linked_transfer_transaction_id);
      
      // For printing, calculate running balance at each point
      let balanceAtTransaction = runningBalance;
      
      for (let j = 0; j < i; j++) {
        const prevT = transactions[j];
        const prevIncoming = prevT.transaction_type === 'deposit' || 
          (prevT.transaction_type === 'transfer' && prevT.linked_transfer_transaction_id);
        
        if (prevIncoming) {
          balanceAtTransaction -= parseFloat(prevT.amount || 0);
        } else {
          balanceAtTransaction += parseFloat(prevT.amount || 0);
        }
      }
      
      transactionsWithBalance.push({
        ...t,
        isIncoming,
        runningBalance: balanceAtTransaction
      });
    }

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${account?.account_name || 'Account'} - Transactions</title>
        <style>
          * { box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            margin: 0;
            padding: 20px;
            color: #333; 
            font-size: 11px;
            line-height: 1.4;
          }
          .header { 
            border-bottom: 2px solid #333; 
            padding-bottom: 15px; 
            margin-bottom: 15px; 
          }
          h1 { 
            font-size: 18px; 
            margin: 0 0 5px 0; 
            color: #000;
          }
          .bank-details { 
            font-size: 11px; 
            color: #555; 
            margin-bottom: 5px; 
          }
          .date-range { 
            font-size: 11px; 
            color: #555; 
          }
          .account-summary { 
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px; 
            padding: 12px;
            border: 1px solid #ddd;
          }
          .summary-item { 
            text-align: center;
          }
          .summary-label { 
            font-size: 10px; 
            color: #666; 
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 3px;
          }
          .summary-value { 
            font-size: 13px; 
            font-weight: bold;
            font-family: 'Courier New', monospace;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            font-size: 10px;
            margin-bottom: 20px;
          }
          th { 
            background: #f5f5f5; 
            text-align: left; 
            padding: 8px 6px; 
            border: 1px solid #ddd;
            font-weight: 600;
            font-size: 9px;
            text-transform: uppercase;
            letter-spacing: 0.3px;
          }
          td { 
            padding: 6px; 
            border: 1px solid #eee;
            vertical-align: top;
          }
          .text-right { text-align: right; }
          .amount-col { 
            text-align: right; 
            font-family: 'Courier New', monospace;
            white-space: nowrap;
          }
          .totals-section {
            border: 1px solid #333;
            padding: 15px;
            margin-top: 20px;
          }
          .totals-title {
            font-size: 11px;
            font-weight: bold;
            text-transform: uppercase;
            margin-bottom: 10px;
            border-bottom: 1px solid #ddd;
            padding-bottom: 5px;
          }
          .totals-grid {
            display: flex;
            justify-content: space-between;
          }
          .totals-item {
            text-align: center;
            flex: 1;
          }
          .totals-label {
            font-size: 10px;
            color: #666;
            margin-bottom: 3px;
          }
          .totals-value {
            font-size: 12px;
            font-weight: bold;
            font-family: 'Courier New', monospace;
          }
          .totals-value.positive { color: #000; }
          .totals-value.negative { color: #333; }
          .footer { 
            margin-top: 25px; 
            padding-top: 10px;
            border-top: 1px solid #ddd;
            font-size: 9px; 
            color: #999; 
            text-align: center; 
          }
          @media print {
            body { 
              margin: 0; 
              padding: 15px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .no-print { display: none; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            thead { display: table-header-group; }
            @page { 
              margin: 1cm;
              size: A4 portrait;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${account?.account_name || 'Account'} - Transaction History</h1>
          <div class="bank-details">
            ${account?.bank_name ? `${account.bank_name}` : ''}${account?.bank_name && (account?.sort_code || account?.account_number) ? ' | ' : ''}${account?.sort_code ? `Sort Code: ${account.sort_code}` : ''}${account?.sort_code && account?.account_number ? ' | ' : ''}${account?.account_number ? `Account: ${account.account_number}` : ''}
          </div>
          <div class="date-range">Report Period: ${dateRange} | Generated: ${new Date().toLocaleString('en-GB')}</div>
        </div>
        
        <div class="account-summary">
          <div class="summary-item">
            <div class="summary-label">Opening Balance</div>
            <div class="summary-value">${formatCurrency(account?.opening_balance)}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Current Balance</div>
            <div class="summary-value">${formatCurrency(account?.current_balance)}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Transactions</div>
            <div class="summary-value">${transactions.length}</div>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th style="width: 70px;">Date</th>
              <th style="width: 70px;">Type</th>
              <th style="width: 90px;">Reference</th>
              <th>Description</th>
              <th style="width: 85px;" class="text-right">Amount</th>
              <th style="width: 90px;" class="text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            ${transactionsWithBalance.map(t => {
              const prefix = t.isIncoming ? '+' : '-';
              return `
                <tr>
                  <td>${formatDate(t.transaction_date)}</td>
                  <td>${getTransactionTypeLabel(t.transaction_type)}</td>
                  <td>${t.reference || '-'}</td>
                  <td>${t.description || '-'}</td>
                  <td class="amount-col">${prefix}${formatCurrency(t.amount)}</td>
                  <td class="amount-col">${formatCurrency(t.runningBalance)}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        
        <div class="totals-section">
          <div class="totals-title">Summary Totals</div>
          <div class="totals-grid">
            <div class="totals-item">
              <div class="totals-label">Total Deposits</div>
              <div class="totals-value positive">+${formatCurrency(totalDeposits)}</div>
            </div>
            <div class="totals-item">
              <div class="totals-label">Total Withdrawals</div>
              <div class="totals-value negative">-${formatCurrency(totalWithdrawals)}</div>
            </div>
            <div class="totals-item">
              <div class="totals-label">Net Change</div>
              <div class="totals-value ${netChange >= 0 ? 'positive' : 'negative'}">${netChange >= 0 ? '+' : ''}${formatCurrency(netChange)}</div>
            </div>
          </div>
        </div>
        
        <div class="footer">
          This report was generated by SparxMate on ${new Date().toLocaleString('en-GB')}
        </div>
        
        <script>
          window.onload = function() { 
            setTimeout(function() { window.print(); }, 250);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  // CSV Export handler
  const handleExportCSV = () => {
    const headers = ['Date', 'Type', 'Reference', 'Description', 'Amount', 'Running Balance'];
    
    // Calculate running balances for all transactions
    let runningBalance = parseFloat(account?.current_balance || 0);
    const transactionsWithBalance = transactions.map((t, index) => {
      const isIncoming = t.transaction_type === 'deposit' || 
        (t.transaction_type === 'transfer' && t.linked_transfer_transaction_id);
      
      // Calculate running balance at this point
      let balanceAtTransaction = runningBalance;
      for (let j = 0; j < index; j++) {
        const prevT = transactions[j];
        const prevIncoming = prevT.transaction_type === 'deposit' || 
          (prevT.transaction_type === 'transfer' && prevT.linked_transfer_transaction_id);
        
        if (prevIncoming) {
          balanceAtTransaction -= parseFloat(prevT.amount || 0);
        } else {
          balanceAtTransaction += parseFloat(prevT.amount || 0);
        }
      }
      
      return {
        ...t,
        isIncoming,
        runningBalance: balanceAtTransaction
      };
    });
    
    const rows = transactionsWithBalance.map(t => {
      const amount = t.isIncoming 
        ? parseFloat(t.amount || 0).toFixed(2)
        : `-${parseFloat(t.amount || 0).toFixed(2)}`;
      
      return [
        formatDate(t.transaction_date),
        getTransactionTypeLabel(t.transaction_type),
        `"${(t.reference || '').replace(/"/g, '""')}"`,
        `"${(t.description || '').replace(/"/g, '""')}"`,
        amount,
        t.runningBalance.toFixed(2)
      ].join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    const accountName = (account?.account_name || 'account').replace(/[^a-z0-9]/gi, '-');
    const dateStr = new Date().toISOString().split('T')[0];
    link.href = url;
    link.download = `${accountName}-transactions-${dateStr}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // PDF Export handler
  const handleExportPDF = async () => {
    if (!account || pdfLoading) return;
    
    try {
      setPdfLoading(true);
      
      // Call API with current filters
      const pdfBlob = await bankingAPI.getAccountTransactionsPDF(accountId, {
        start_date: dateFilters.start_date,
        end_date: dateFilters.end_date,
        search: search,
        type: typeFilter,
      });
      
      // Create download link
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      const accountName = (account?.account_name || 'account').replace(/[^a-z0-9]/gi, '-');
      const dateStr = new Date().toISOString().split('T')[0];
      link.href = url;
      link.download = `${accountName}-transactions-${dateStr}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(err.message || 'Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <Layout>
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/tenant/banking')}
                  className="text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              </div>
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                {account?.account_name || 'Loading...'}
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                {account ? getAccountTypeLabel(account.account_type) : ''}
                {account?.bank_name ? ` • ${account.bank_name}` : ''}
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                disabled={!account || transactions.length === 0}
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                disabled={!account || transactions.length === 0 || pdfLoading}
              >
                <FileText className="h-4 w-4 mr-2" />
                {pdfLoading ? 'Generating...' : 'Export PDF'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                disabled={!account || transactions.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Account Summary Card */}
        {account && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <Card>
              <CardContent className="p-6">
                <div className="text-meta font-medium text-[var(--ds-text-muted)]">Current Balance</div>
                <div className={`mt-2 text-h1 font-bold tabular-nums ${
                  parseFloat(account.current_balance || 0) >= 0 
                    ? 'text-[var(--ds-text-primary)]' 
                    : 'text-[var(--ds-status-danger)]'
                }`}>
                  {formatCurrency(account.current_balance)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-meta font-medium text-[var(--ds-text-muted)]">Opening Balance</div>
                <div className="mt-2 text-h1 font-bold text-[var(--ds-text-primary)] tabular-nums">
                  {formatCurrency(account.opening_balance)}
                </div>
                {account.opening_balance_date && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    as of {formatDate(account.opening_balance_date)}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-meta font-medium text-[var(--ds-text-muted)]">Bank Details</div>
                <div className="mt-2 text-xs text-gray-900 dark:text-gray-100">
                  {account.bank_name || '-'}
                </div>
                {(account.sort_code || account.account_number) && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {account.sort_code && `SC: ${account.sort_code}`}
                    {account.sort_code && account.account_number && ' • '}
                    {account.account_number && `AC: ${account.account_number}`}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-meta font-medium text-[var(--ds-text-muted)]">Status</div>
                <div className="mt-2">
                  <Badge variant={account.is_active ? 'success' : 'neutral'}>
                    {account.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  {account.is_system_account && (
                    <Badge variant="neutral" className="ml-2">
                      System Account
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="md:col-span-2">
                <FormLabel htmlFor="search-filter">Search</FormLabel>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[var(--ds-text-muted)]" />
                  <Input
                    id="search-filter"
                    type="text"
                    placeholder="Search reference, description, notes..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="pl-10 h-8"
                  />
                </div>
              </div>
              <div>
                <FormLabel htmlFor="start-date-filter">Start Date</FormLabel>
                <Input
                  id="start-date-filter"
                  type="date"
                  value={dateFilters.start_date}
                  onChange={(e) => {
                    setDateFilters(prev => ({ ...prev, start_date: e.target.value }));
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="h-8"
                />
              </div>
              <div>
                <FormLabel htmlFor="end-date-filter">End Date</FormLabel>
                <Input
                  id="end-date-filter"
                  type="date"
                  value={dateFilters.end_date}
                  onChange={(e) => {
                    setDateFilters(prev => ({ ...prev, end_date: e.target.value }));
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="h-8"
                />
              </div>
              <div>
                <FormLabel htmlFor="type-filter">Type</FormLabel>
                <Select
                  id="type-filter"
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="h-8"
                >
                  <option value="">All Types</option>
                  <option value="deposit">Deposit</option>
                  <option value="withdrawal">Withdrawal</option>
                  <option value="transfer">Transfer</option>
                  <option value="manual">Manual</option>
                </Select>
              </div>
            </div>
            {hasActiveFilters && (
              <div className="mt-4 flex items-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                  className="text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear Filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)] mb-4">
            {error}
          </div>
        )}

        {/* Transactions Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-8 text-center text-xs text-gray-500 dark:text-gray-400">
              <p>No transactions found</p>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                  className="mt-4"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow isHeader>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead align="right">Amount</TableHead>
                    <TableHead align="right">Running Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((transaction, index) => {
                    const isIncoming = transaction.transaction_type === 'deposit' || 
                      (transaction.transaction_type === 'transfer' && transaction.linked_transfer_transaction_id);
                    const runningBalance = getRunningBalance(index);
                    
                    return (
                      <TableRow 
                        key={transaction.id} 
                        className="cursor-pointer"
                        onClick={() => handleTransactionClick(transaction.id)}
                      >
                        <TableCell>
                          <div className="text-xs text-gray-900 dark:text-gray-100">
                            {formatDate(transaction.transaction_date)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={
                              transaction.transaction_type === 'deposit' ? 'success' :
                              transaction.transaction_type === 'withdrawal' ? 'danger' :
                              transaction.transaction_type === 'transfer' ? 'info' :
                              'neutral'
                            }
                            className="text-xs"
                          >
                            {getTransactionTypeLabel(transaction.transaction_type)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {transaction.reference || '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs text-gray-900 dark:text-gray-100">
                            {transaction.description || '-'}
                          </div>
                          {transaction.notes && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {transaction.notes}
                            </div>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <div className={`text-xs font-medium tabular-nums ${getTransactionTypeColor(transaction.transaction_type, isIncoming)}`}>
                            {isIncoming ? '+' : '-'}{formatCurrency(transaction.amount)}
                          </div>
                        </TableCell>
                        <TableCell align="right">
                          <div className={`text-xs font-medium tabular-nums ${
                            runningBalance >= 0 
                              ? 'text-gray-900 dark:text-gray-100' 
                              : 'text-red-600 dark:text-red-400'
                          }`}>
                            {formatCurrency(runningBalance)}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-4 py-3 sm:px-6 border-t border-[var(--ds-border-divider)]">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                      <span className="font-medium">{pagination.totalPages}</span>
                      {' '}({pagination.total} total transactions)
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page === pagination.totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Transaction Detail Slide-Over */}
      <TransactionDetailSlideOver
        open={showDetailSlideOver}
        onOpenChange={handleDetailSlideOverClose}
        transactionId={selectedTransactionId}
        onEdit={handleEditTransaction}
        onDelete={handleDeleteTransaction}
        onVoid={handleVoidTransaction}
      />

      {/* Delete Transaction Confirmation Dialog */}
      <DeleteTransactionDialog
        open={showDeleteDialog}
        onOpenChange={handleDeleteDialogClose}
        transaction={transactionToDelete}
        onConfirm={confirmDeleteTransaction}
        isLoading={deleteLoading}
      />

      {/* Edit Transaction Form */}
      <TransactionEditForm
        open={showEditForm}
        onOpenChange={handleEditFormClose}
        transaction={transactionToEdit}
        onSuccess={handleEditSuccess}
      />

      {/* Void Transaction Dialog */}
      <VoidTransactionDialog
        open={showVoidDialog}
        onOpenChange={handleVoidDialogClose}
        transaction={transactionToVoid}
        onConfirm={confirmVoidTransaction}
        isLoading={voidLoading}
      />
    </Layout>
  );
};

export default BankingAccountTransactions;
